import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  IconButton,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorPage from "../Components/ErrorPage";
import StockAdd from "./StockAdd";
import StockEdit from "./StockEdit";
import { useTranslation } from "react-i18next";
import { exportStockToPDF, exportStockToExcel, exportStockToCSV, printStockList } from "../../utils/exportStock";

const getAvailableQty = (stock) => {
  const rawQty = stock?.availableQty ?? stock?.quantity ?? 0;
  return Number(rawQty) || 0;
};

const normalizeCurrencyCode = (value) => {
  const code = String(value || "").trim().toUpperCase();
  if (code === "AFN" || code === "AFG") return "AFG";
  if (code === "USD") return "USD";
  return code;
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(2);
};

const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const normalizeImportHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

const normalizeImportCurrency = (value) => {
  const code = String(value || "").trim().toUpperCase();
  if (code === "AFN" || code === "AFG") return "AFG";
  if (code === "USD") return "USD";
  return code;
};

const getStockCurrencyCode = (stock) =>
  normalizeCurrencyCode(
    stock?.currency_details?.title ||
      stock?.purchase_item_details?.purchase?.currency?.title ||
      stock?.purchase_item_details?.currency_details?.title
  );

const buildImportRowsFromSheet = (sheet) => {
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
  });
  if (!rows.length) return [];

  const findHeaderRowIndex = () => {
    const maxScan = Math.min(rows.length, 20);
    for (let i = 0; i < maxScan; i += 1) {
      const normalized = (rows[i] || []).map(normalizeImportHeader);
      const hasProduct = normalized.some((value) =>
        ["product", "product_name", "item", "item_name", "name"].includes(value)
      );
      const hasQty = normalized.some((value) =>
        ["quantity", "qty", "available_qty", "available_quantity", "stock_qty"].includes(value)
      );
      const hasPrice = normalized.some((value) =>
        ["buy_price", "cost_price", "purchase_price", "wholesale_price", "retail_price"].includes(value)
      );
      if (hasProduct && (hasQty || hasPrice)) {
        return i;
      }
    }
    return 0;
  };

  const headerRowIndex = findHeaderRowIndex();
  const headerRow = rows[headerRowIndex] || [];
  const nextRow = rows[headerRowIndex + 1] || [];
  const hasCurrencySubHeader = nextRow.some((cell) => {
    const normalized = normalizeImportCurrency(cell);
    return normalized === "AFG" || normalized === "USD";
  });

  const mapHeaderAlias = (value) => {
    const key = normalizeImportHeader(value);
    if (["product", "product_name", "productname", "item", "item_name", "name"].includes(key)) return "product";
    if (["code", "product_code", "productcode", "item_code"].includes(key)) return "code";
    if (["supplier", "supplier_name", "vendor"].includes(key)) return "supplier";
    if (["unit", "unit_title", "uom"].includes(key)) return "unit";
    if (["branch", "branch_name", "shop", "shop_name"].includes(key)) return "branch";
    if (["quantity", "qty", "available_qty", "available_quantity", "stock_qty"].includes(key)) return "available_qty";
    if (["buy_price", "cost_price", "purchase_price"].includes(key)) return "buy_price";
    if (["wholesale_price", "wholesale"].includes(key)) return "wholesale_price";
    if (["retail_price", "selling_price"].includes(key)) return "retail_price";
    if (["sale_price"].includes(key)) return "sale_price";
    if (["currency", "currency_title"].includes(key)) return "currency";
    return key;
  };

  let activePriceGroup = "";
  const keys = headerRow.map((cell, index) => {
    const mapped = mapHeaderAlias(cell);
    if (["buy_price", "wholesale_price", "retail_price"].includes(mapped)) {
      activePriceGroup = mapped;
    } else if (mapped) {
      activePriceGroup = "";
    }

    const currencyCode = hasCurrencySubHeader
      ? normalizeImportCurrency(nextRow[index])
      : "";
    if (
      activePriceGroup &&
      (currencyCode === "AFG" || currencyCode === "USD")
    ) {
      return `${activePriceGroup}_${currencyCode.toLowerCase()}`;
    }
    return mapped || `__col_${index}`;
  });

  const dataStartIndex = headerRowIndex + (hasCurrencySubHeader ? 2 : 1);
  return rows
    .slice(dataStartIndex)
    .filter((row) => row.some((cell) => String(cell || "").trim() !== ""))
    .map((row) => {
      const obj = {};
      keys.forEach((key, index) => {
        obj[key] = row[index] ?? "";
      });
      return obj;
    });
};

const getAverageBuyPrice = (stock) => {
  const raw =
    stock?.average_buy_price ??
    stock?.purchase_item_details?.average_buy_price ??
    stock?.purchase_item_details?.buy_price ??
    0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const getStockValue = (stock) => {
  const quantity = getAvailableQty(stock);
  return quantity * getAverageBuyPrice(stock);
};

const StockRow = ({ stock, allStocks, onEdit, onDelete, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isInternalTransferStock =
    Boolean(stock?.is_transfer_stock) ||
    String(stock?.purchase_item_details?.purchase?.supplier?.name || "")
      .trim()
      .startsWith("Transfer from ");
  const getPriceByCurrency = (field, currencyCode) => {
    const overrideFieldMap = {
      buy_price: { AFG: "buy_afg", USD: "buy_usd" },
      wholesale_price: { AFG: "wholesale_afg", USD: "wholesale_usd" },
      retail_price: { AFG: "retail_afg", USD: "retail_usd" },
    };
    const normalizedCurrency = normalizeCurrencyCode(currencyCode);
    const overrideField = overrideFieldMap[field]?.[normalizedCurrency];
    const overrideValue = overrideField ? stock?.__priceOverrides?.[overrideField] : null;
    if (overrideValue != null && overrideValue !== "") return overrideValue;

    const rowCurrency = getStockCurrencyCode(stock);
    const directRowValue =
      stock?.purchase_item_details?.[field] ??
      (field === "retail_price" ? stock?.purchase_item_details?.sale_price : null);
    if (
      rowCurrency &&
      rowCurrency === normalizedCurrency &&
      directRowValue != null &&
      directRowValue !== ""
    ) {
      return directRowValue;
    }

    const extraKeyMap = {
      buy_price: { AFG: "buy_price_afg", USD: "buy_price_usd" },
      wholesale_price: { AFG: "wholesale_price_afg", USD: "wholesale_price_usd" },
      retail_price: { AFG: "retail_price_afg", USD: "retail_price_usd" },
    };
    const extraField = extraKeyMap[field]?.[normalizedCurrency];
    const extraValue = extraField
      ? stock?.purchase_item_details?.[extraField]
      : null;
    if (extraValue != null && extraValue !== "") return extraValue;

    const productId = stock?.purchase_item_details?.product?.id;
    if (!productId) return "-";
    const sameProductStocks = (allStocks || []).filter(
      (s) => s?.purchase_item_details?.product?.id === productId
    );
    const target = sameProductStocks.find(
      (s) => getStockCurrencyCode(s) === normalizeCurrencyCode(currencyCode)
    );
    const value =
      target?.purchase_item_details?.[field] ??
      (field === "retail_price" ? target?.purchase_item_details?.sale_price : null);
    return value ?? "-";
  };

  const getPriceLines = (field) => {
    const rowCurrency = getStockCurrencyCode(stock);
    const preferredCurrency = rowCurrency || "AFG";
    const preferredValue =
      stock?.purchase_item_details?.[field] ??
      (field === "retail_price" ? stock?.purchase_item_details?.sale_price : null);
    const resolvedValue =
      preferredValue != null && preferredValue !== ""
        ? preferredValue
        : getPriceByCurrency(field, preferredCurrency);

    if (resolvedValue == null || resolvedValue === "") return [];
    const normalizedValue = formatMoney(resolvedValue);
    if (normalizedValue === "-" || normalizedValue === "0.00") return [];
    return [{ code: preferredCurrency, value: normalizedValue }];
  };

  const renderStockDetails = () => {
    return (
      <Table size="small" aria-label="stock details">
        <TableHead>
          <TableRow>
            <TableCell>{t("stock.product")}</TableCell>
            <TableCell>{t("stock.quantity")}</TableCell>
            <TableCell>{t("stock.buyPrice")}</TableCell>
            <TableCell>Wholesale Price</TableCell>
            <TableCell>Retail Price</TableCell>
            <TableCell>{t("stock.totalStockValue")}</TableCell>
            <TableCell>{t("stock.unit")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              {stock?.purchase_item_details?.product?.name || "--"}
            </TableCell>
            <TableCell>
              {getAvailableQty(stock) || "-"}
            </TableCell>
            <TableCell sx={{ color: "#e65100", fontWeight: 600 }}>
              {formatMoney(getAverageBuyPrice(stock))}
            </TableCell>
            <TableCell sx={{ color: "#1b5e20", fontWeight: 600 }}>
              {stock?.purchase_item_details?.wholesale_price || "-"}
            </TableCell>
            <TableCell sx={{ color: "#6a1b9a", fontWeight: 600 }}>
              {stock?.purchase_item_details?.retail_price || stock?.purchase_item_details?.sale_price || "0.00"}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {formatMoney(getStockValue(stock))}
            </TableCell>
            <TableCell>
              {stock?.unit_details?.title || "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {index}
        </TableCell>
        <TableCell>
          {stock?.purchase_item_details?.product?.code ||
            stock?.purchase_item_details?.product?.product_code ||
            (stock?.purchase_item_details?.product?.id
              ? String(stock.purchase_item_details.product.id)
              : "--")}
        </TableCell>
        <TableCell>
          {stock?.purchase_item_details?.product?.name || "--"}
        </TableCell>
        <TableCell>
          {stock?.purchase_item_details?.purchase?.supplier?.name || "--"}
        </TableCell>
        <TableCell sx={{ color: "#e65100", fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.45 }}>
          {getPriceLines("buy_price").map((line) => (
            <Box key={`buy-${line.code}`}>{line.code}: {line.value}</Box>
          ))}
        </TableCell>
            <TableCell sx={{ color: "#1b5e20", fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.45 }}>
          {getPriceLines("wholesale_price").map((line) => (
            <Box key={`wholesale-${line.code}`}>{line.code}: {line.value}</Box>
          ))}
        </TableCell>
        <TableCell sx={{ color: "#6a1b9a", fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.45 }}>
          {getPriceLines("retail_price").map((line) => (
            <Box key={`retail-${line.code}`}>{line.code}: {line.value}</Box>
          ))}
        </TableCell>
        <TableCell>{getAvailableQty(stock)}</TableCell>
        <TableCell>{stock?.unit_details?.title || "-"}</TableCell>
        <TableCell>{stock?.shop_details?.name || "-"}</TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            onClick={() => onEdit(stock)}
          >
            {t("stock.edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => onDelete(stock.id)}
          >
            {t("stock.delete")}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("stock.purchaseItemsDetail")}
              </Typography>
              {renderStockDetails()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const StockIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const branchUserSelectedShop = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : "";

  // Pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(branchUserSelectedShop);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [retryCount, setRetryCount] = useState(0);
  const [showAllError, setShowAllError] = useState(false);

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);

  useEffect(() => {
    if (!importMessage) return undefined;
    const timer = setTimeout(() => {
      setImportMessage(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [importMessage]);

  const { data: shopsData } = useFetchObjects(
    ["stock-branches"],
    "shop/shop/?page_size=all",
    token
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  // Fetch products that are in stock for filter dropdown with smaller page size
  const { data: stockProducts } = useFetchObjects(
    ["stockProducts", selectedBranch],
    `shop/stocks/?page_size=1000${selectedBranch ? `&shop=${selectedBranch}` : ""}`,
    token
  );
  
  const { data: exchangeRatesData } = useFetchObjects(
    ["exchangeRatesForStock"],
    "shop/exchange-rates/?page_size=500", // Reduced to prevent timeout
    token
  );
  
  const buildSummaryUrl = () => {
    const params = new URLSearchParams();
    if (selectedBranch) {
      params.append("shop", selectedBranch);
    }
    if (searchTerm) {
      params.append("search", searchTerm);
    }
    if (productFilter) {
      params.append("purchase_item__product", productFilter);
    }
    const query = params.toString();
    return `shop/stocks/summary/${query ? `?${query}` : ""}`;
  };
  
  const { data: stockSummary, refetch: refetchStockSummary } = useFetchObjects(
    ["stockSummary", selectedBranch, searchTerm, productFilter],
    buildSummaryUrl(),
    token
  );

  // Extract unique products from stock data
  const uniqueProducts = useMemo(() => {
    if (!stockProducts?.results) return [];
    const productMap = new Map();
    stockProducts.results.forEach(stock => {
      if (stock.purchase_item_details?.product) {
        const product = stock.purchase_item_details.product;
        productMap.set(product.id, product);
      }
    });
    return Array.from(productMap.values());
  }, [stockProducts]);

  // Build API URL with filters - MAX page size is 1000 to prevent timeout
  const buildApiUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedBranch) {
      params.append("shop", selectedBranch);
    }
    
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    if (productFilter) {
      params.append('purchase_item__product', productFilter);
    }
    
    // IMPORTANT: Never use 'all' - use 1000 as maximum to prevent timeout
    if (rowsPerPage === 'all') {
      params.append('page_size', '1000'); // Max 1000 to prevent connection reset
      params.append('page', '1');
    } else {
      params.append('page', page.toString());
      params.append('page_size', rowsPerPage.toString());
    }
    
    const url = `shop/stocks/?${params.toString()}`;
    return url;
  }, [selectedBranch, searchTerm, productFilter, page, rowsPerPage]);

  // Fetch stock records with search and pagination parameters
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["stock", selectedBranch, searchTerm, productFilter, page, rowsPerPage, retryCount],
    buildApiUrl(),
    token
  );

  const handlePermanentDelete = async (id) => {
    if (!id) return;
    if (!window.confirm(t("api.confirmMessage") || "Are you sure you want to delete this item?")) return;

    try {
      const headers = auth?.buildAuthHeader?.() || (token ? { Authorization: `Token ${token}` } : {});
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/shop/stocks/${id}/?permanent=true`, {
        headers,
      });
      setImportMessage({ type: "success", text: t("api.deleteSuccess") || "Deleted successfully." });
      refetch();
      refetchStockSummary();
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || t("api.failedDelete") || "Failed to delete.";
      setImportMessage({ type: "error", text: message });
    }
  };

  // Refetch whenever search, product, page or rowsPerPage changes
  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, selectedBranch, searchTerm, productFilter, refetch, retryCount]);

  useEffect(() => {
    refetchStockSummary();
  }, [selectedBranch, searchTerm, productFilter, refetchStockSummary]);

  // Check if error is due to large data
  useEffect(() => {
    if (error) {
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('ERR_HTTP2_PROTOCOL_ERROR') || 
          errorMessage.includes('ERR_CONNECTION_RESET') ||
          errorMessage.includes('timeout')) {
        setShowAllError(true);
        // Auto retry with smaller page size
        if (rowsPerPage === 'all') {
          setRowsPerPage(100);
          setPage(1);
          setRetryCount(prev => prev + 1);
        }
      } else {
        setShowAllError(false);
      }
    }
  }, [error, rowsPerPage]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleProductChange = (e) => {
    setProductFilter(e.target.value);
    setPage(1);
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    setProductFilter("");
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      // Show warning before loading all
      if (window.confirm(t('stock.loadAllWarning') || 'Loading all records may cause performance issues. Continue?')) {
        setRowsPerPage('all');
        setPage(1);
        setShowAllError(false);
      }
    } else {
      setRowsPerPage(parseInt(value));
      setPage(1);
      setShowAllError(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setShowAllError(false);
    refetch();
  };

  // Get stocks list from response
  const stocks = data?.results || data || [];
  
  // Use allStocks from the main data
  const allStockRows = useMemo(() => {
    if (data?.results) {
      return data.results;
    }
    return Array.isArray(data) ? data : [];
  }, [data]);

  const exchangeRates = exchangeRatesData?.results || exchangeRatesData || [];

  const latestRateMap = useMemo(() => {
    const map = new Map();
    for (const rate of exchangeRates) {
      const from = normalizeCurrencyCode(rate?.from_currency_title || rate?.from_currency?.title);
      const to = normalizeCurrencyCode(rate?.to_currency_title || rate?.to_currency?.title);
      const value = Number(rate?.rate);
      if (!from || !to || !Number.isFinite(value) || value <= 0) continue;
      const key = `${from}->${to}`;
      const prev = map.get(key);
      const rateDate = String(rate?.rate_date || "");
      if (!prev || rateDate > prev.rateDate) {
        map.set(key, { rate: value, rateDate });
      }
    }
    return map;
  }, [exchangeRates]);

  const convertPrice = (amount, fromCurrency, toCurrency) => {
    const from = normalizeCurrencyCode(fromCurrency);
    const to = normalizeCurrencyCode(toCurrency);
    if (!Number.isFinite(Number(amount))) return null;
    if (from === to) return Number(amount);
    const direct = latestRateMap.get(`${from}->${to}`);
    if (direct?.rate) return Number(amount) * direct.rate;
    const reverse = latestRateMap.get(`${to}->${from}`);
    if (reverse?.rate) return Number(amount) / reverse.rate;
    return null;
  };

  // Only filter by available quantity > 0
  const visibleStocks = useMemo(() => {
    if (!stocks || !Array.isArray(stocks)) return [];
    return stocks.filter((stock) => getAvailableQty(stock) > 0);
  }, [stocks]);

  const stockValueLines = (stockSummary?.stock_value || [])
    .map((row) => `${formatMoney(row?.amount)} ${row?.currency || ""}`.trim());

  const overviewCards = [
    {
      title: "Stock Items",
      subtitle: "Available products",
      value: formatNumber(stockSummary?.item_count),
      color: "#0f4c81",
    },
    {
      title: "Total Available Qty",
      subtitle: "Quantity currently in stock",
      value: formatNumber(stockSummary?.available_qty),
      color: "#2e7d32",
    },
    {
      title: "Total Stock Value",
      subtitle: "Weighted stock value by currency",
      value: stockValueLines.length ? stockValueLines : ["00.00"],
      color: "#e65100",
    },
    {
      title: "Stock Status",
      subtitle: "Current stock state",
      value: stockSummary?.status || "Out of Stock",
      color: "#6a1b9a",
    },
  ];

  // Correctly calculate total count from the API response
  const totalCount = data?.count || (Array.isArray(data) ? data.length : 0);
  const effectiveRowsPerPage = rowsPerPage === 'all' ? 1000 : rowsPerPage;
  const totalPages = rowsPerPage === 'all' ? 1 : Math.ceil(totalCount / effectiveRowsPerPage);

  // Export handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportPDF = () => {
    exportStockToPDF(allStockRows, searchTerm, productFilter);
    handleExportClose();
  };

  const handleExportExcel = () => {
    const allRows = stockProducts?.results || allStockRows;
    exportStockToExcel(allRows);
    handleExportClose();
  };

  const handleExportCSV = () => {
    exportStockToCSV(allStockRows);
    handleExportClose();
  };

  const handlePrint = () => {
    printStockList(allStockRows);
    handleExportClose();
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportMessage(null);
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = buildImportRowsFromSheet(sheet);

      if (!rows.length) {
        setImportMessage({ type: "error", text: "Excel file is empty." });
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/shop/stocks/import_excel/`,
        { rows, shop: selectedBranch || undefined },
        { 
          headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          timeout: 60000 // 60 second timeout for large imports
        }
      );
      
      // Sync product CODE values from the same Excel rows
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/shop/stocks/sync_codes/`,
        { rows, shop: selectedBranch || undefined },
        { 
          headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          timeout: 60000
        }
      );
      
      const created = response?.data?.created || 0;
      const skippedRows = response?.data?.skipped || [];
      const skipped = skippedRows.length || 0;
      const topReasons = [...new Set(skippedRows.map((row) => row?.reason).filter(Boolean))]
        .slice(0, 3)
        .join(", ");
      setImportMessage({
        type: "success",
        text: `Imported ${created} row(s). Skipped ${skipped} row(s).${topReasons ? ` Reason: ${topReasons}.` : ""}`,
      });
      refetch();
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || "Failed to import Excel data.";
      setImportMessage({ type: "error", text: message });
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  if (isError && !showAllError) {
    return <ErrorPage message={t("stock.failedToLoadStocks")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("stock.stockList")}</Typography>
        
        {/* Export Actions */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={importing}
            sx={{
              background: "linear-gradient(45deg, #0f9d58 30%, #34a853 90%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(45deg, #0d8b4d 30%, #2f974c 90%)",
              },
            }}
          >
            {importing ? "Importing..." : "Import Excel"}
            <input hidden type="file" accept=".xlsx,.xls" onChange={handleExcelImport} />
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            sx={{
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)",
              },
            }}
          >
            {t("stock.export")}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            {t("stock.print")}
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            disabled={isLoading}
            sx={{
              borderColor: "#ff9800",
              color: "#ff9800",
              "&:hover": {
                borderColor: "#f57c00",
                backgroundColor: "rgba(255, 152, 0, 0.04)",
              },
            }}
          >
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={exportMenuOpen}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportPDF")} />
        </MenuItem>
        
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <TableViewIcon sx={{ color: "#2e7d32" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportExcel")} />
        </MenuItem>
        
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <DescriptionIcon sx={{ color: "#ed6c02" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportCSV")} />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon sx={{ color: "#1976d2" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.print")} />
        </MenuItem>
      </Menu>
      
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("stock.dataLoadedOn")} {new Date().toLocaleString()}
        {showAllError && (
          <Chip 
            label="Connection issue detected. Showing limited data." 
            color="warning" 
            size="small" 
            sx={{ ml: 2 }}
          />
        )}
      </Typography>
      
      {importMessage && (
        <Alert severity={importMessage.type} sx={{ mb: 2 }}>
          {importMessage.text}
        </Alert>
      )}
      
      {/* Error Alert for Connection Issues */}
      {showAllError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          Connection timeout or data too large. Trying with smaller page size. 
          <Button 
            color="inherit" 
            size="small" 
            onClick={() => {
              setRowsPerPage(50);
              setPage(1);
              setShowAllError(false);
            }}
            sx={{ ml: 1 }}
          >
            Load 50 per page
          </Button>
        </Alert>
      )}
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {overviewCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={`${card.title}-${card.subtitle}`}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 1,
                borderLeft: `4px solid ${card.color}`,
                minHeight: 112,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: card.color, fontVariantNumeric: "tabular-nums" }}
              >
                {Array.isArray(card.value)
                  ? card.value.map((line) => (
                      <Box component="span" key={line} sx={{ display: "block", lineHeight: 1.25 }}>
                        {line}
                      </Box>
                    ))
                  : card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      {/* Search and Filter Fields */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <Select
              value={selectedBranch}
              onChange={handleBranchChange}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="">
                {t("stock.allBranches", "All Warehouses")}
              </MenuItem>
              {shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder={t("stock.searchStocks")}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={productFilter}
              onChange={handleProductChange}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="">
                {t("stock.allProducts") || "All Products"}
              </MenuItem>
              {uniqueProducts.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      {/* Stock Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="stock table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("stock.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>CODE</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.product")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.supplier")}</TableCell>
              <TableCell sx={{ color: "white" }} align="center">{t("stock.buyPrice")}</TableCell>
              <TableCell sx={{ color: "white" }} align="center">Wholesale Price</TableCell>
              <TableCell sx={{ color: "white" }} align="center">Retail Price</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.availableQty")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.unit")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.shop")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.action")}</TableCell>
            </TableRow>
          </TableHead>



          
          <TableBody>


            
            {isLoading ? (
              Array.from(new Array(Math.min(5, rowsPerPage === 'all' ? 10 : rowsPerPage))).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {Array.from(new Array(11)).map((_, colIndex) => (
                    <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : visibleStocks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No stocks available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleStocks.map((stock, index) => {
                const mergedStock = {
                  ...stock,
                };

                // Calculate proper row number based on pagination
                let rowNumber;
                if (rowsPerPage === 'all') {
                  rowNumber = index + 1;
                } else {
                  rowNumber = (page - 1) * effectiveRowsPerPage + index + 1;
                }

                return (
                  <StockRow
                    key={stock.id || `stock-${index}`}
                    stock={mergedStock}
                    allStocks={allStockRows}
                    index={rowNumber}
                    onEdit={(data) => {
                      setEditData(data);
                      setOpenEdit(true);
                    }}
                    onDelete={handlePermanentDelete}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
        spacing={2}
      >
        <Grid item>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2">{t("stock.rowsPerPage")}</Typography>
            <FormControl
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            >
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={"all"}>All (Max 1000)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item>
          {rowsPerPage !== 'all' && totalCount > 0 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          )}
        </Grid>
        <Grid item>
          <Typography variant="body2" color="textSecondary">
            {rowsPerPage === 'all' 
              ? `Showing up to ${Math.min(totalCount, 1000)} of ${totalCount} items` 
              : `Showing ${Math.min((page - 1) * effectiveRowsPerPage + 1, totalCount)} - ${Math.min(page * effectiveRowsPerPage, totalCount)} of ${totalCount}`}
          </Typography>
        </Grid>
      </Grid>
      
      {rowsPerPage === 'all' && totalCount > 1000 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Alert severity="info" variant="outlined">
            Showing first 1000 items. Use filters to narrow results or select a smaller page size.
          </Alert>
        </Box>
      )}
      
      {openAdd && (
        <StockAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      
      {openEdit && (
        <StockEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

    </Box>
  );
};

export default StockIndex;

